import React, { useMemo, useState } from "react";
import { useEffect } from "react";
import {
  Typography,
  Box,
  Button,
  Paper,
  Icon,
  useMediaQuery,
  Table,
  TableBody,
  TableContainer,
  TableCell,
  TableRow,
  Tooltip,
  IconButton,
} from "@mui/material";
import PropTypes from "prop-types";
import { addEmployee } from "services/api";
import GaugeChart from "react-gauge-chart";
import { useMaterialUIController } from "context";
import { TrendingUp, TrendingDown, TrendingFlat, ErrorOutlineRounded } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@emotion/react";
import EmployeeAvatar from "components/EmployeeAvatar";
import { useTranslation } from "react-i18next";
import WorkIcon from "@mui/icons-material/Work";
import CalculateIcon from "@mui/icons-material/Calculate";

const GAUGE_WIDTH = 150;
const GAUGE_HEIGHT = 80;

const getTrendIcon = (trend) => {
  if (!trend || trend == "null") return <ErrorOutlineRounded sx={{ color: "gray" }} />;
  if (trend > 0) {
    return <TrendingUp sx={{ color: "green" }} />;
  } else if (trend < 0) {
    return <TrendingDown sx={{ color: "red" }} />;
  } else {
    return <TrendingFlat sx={{ color: "gray" }} />;
  }
};

const ListView = ({ month, year, setSelectedEmployee, myTeam, myIndividualData }) => {
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // Map myTeam
  const employees = useMemo(() => {
    return myTeam?.map((item, index) => {
      const [e_id, e_name, , avg_result, trend, photo, job] = item; // Destructure the array directly
      const gauge =
        avg_result && avg_result !== "null" ? (
          <Box width={GAUGE_WIDTH} height={GAUGE_HEIGHT}>
            <GaugeChart
              id={`gauge-chart-${index}`}
              nrOfLevels={30}
              percent={parseFloat(avg_result) / 100 > 1 ? 1 : parseFloat(avg_result) / 100}
              formatTextValue={() => `${parseFloat(avg_result).toFixed(1)}%`}
              textColor={darkMode ? "#fff" : "#000"}
              needleColor="#666"
              needleBaseColor="#666"
              animate={false}
              colors={["#FF5F00", "#FFDD00", "#00B100"]}
              arcWidth={0.2}
              arcPadding={0.02}
            />
          </Box>
        ) : (
          <Box width={GAUGE_WIDTH} height={GAUGE_HEIGHT}>
            <GaugeChart
              nrOfLevels={30}
              hideText={true}
              animate={false}
              arcWidth={0.2}
              arcPadding={0.02}
              percent={0}
              needleColor="#ccc"
              needleBaseColor="#ccc"
              colors={["#ddd", "#ddd", "#ddd"]} // Make the arc gray to indicate no data
            />
          </Box>
        );
      return {
        e_id,
        e_name,
        avg_result: avg_result !== null ? avg_result : 0, // Handle null values
        trend: trend !== null ? trend : null, // Handle null values
        photo,
        job,
        gauge,
        index,
      };
    });
  }, [myTeam]);

  // Map myIndividualData
  const my_data = useMemo(() => {
    return myIndividualData?.map((item) => {
      const [, my_name, , avg_result, trend, photo, job] = item; // Destructure the array directly
      const gauge =
        avg_result && avg_result !== "null" ? (
          <Box width={GAUGE_WIDTH} height={GAUGE_HEIGHT}>
            {" "}
            {/* Adjust size here */}
            <GaugeChart
              id={`gauge-chart-myinfo`}
              nrOfLevels={30}
              percent={parseFloat(avg_result) / 100 > 1 ? 1 : parseFloat(avg_result) / 100}
              formatTextValue={() => `${parseFloat(avg_result).toFixed(1)}%`}
              textColor={darkMode ? "#fff" : "#000"}
              needleColor="#666"
              needleBaseColor="#666"
              animate={false}
              colors={["#FF5F00", "#FFDD00", "#00B100"]}
              arcWidth={0.2}
              arcPadding={0.02}
            />
          </Box>
        ) : (
          <Box width={GAUGE_WIDTH} height={GAUGE_HEIGHT}>
            {" "}
            {/* Same size for placeholder gauge */}
            <GaugeChart
              nrOfLevels={30}
              hideText={true}
              animate={false}
              arcWidth={0.2}
              arcPadding={0.02}
              percent={0}
              needleColor="#ccc"
              needleBaseColor="#ccc"
              colors={["#ddd", "#ddd", "#ddd"]}
            />
          </Box>
        );

      return {
        my_name,
        avg_result: avg_result !== null ? avg_result : 0, // Handle null values
        trend: trend !== null ? trend : null, // Handle null values
        photo,
        job,
        gauge,
      };
    })[0];
  }, [myIndividualData]);

  const navigate = useNavigate(); // Initialize navigation
  return (
    <Box p={3} mt={0.5}>
      <TableContainer component={Paper} elevation={3}>
        <Box
          sx={{
            maxHeight: 500, // adjust height as needed
            overflowY: "auto",
            border: "1px solid #ccc",
            borderRadius: 2,
          }}
        >
          <Table>
            <TableBody>
              {/* Header Row */}
              <TableRow
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2, // higher than row shadows
                  backgroundColor: darkMode ? "#2d2d2d" : "#f0f9ff", // Soft background color
                  borderLeft: "4px solid", // Subtle accent bar
                  borderLeftColor: darkMode ? "#29b6f6" : "#0288d1", // Info-themed accent
                  boxShadow: darkMode
                    ? "inset 0 0 0 9999px rgba(255, 255, 255, 0.02)"
                    : "0px 2px 6px rgba(0, 0, 0, 0.05)",
                  fontWeight: "500",
                }}
              >
                {[
                  t("user.myTeam.photo"),
                  t("user.assess.columns.name"),
                  t("admin.dashboard.job"),
                  t("user.dashboards.gaugeChart"),
                  t("user.assess.columns.Trend"),
                  t("user.assess.columns.actions"),
                ].map((label, index) => (
                  <TableCell
                    key={index}
                    align="center"
                    colSpan={index === 5 ? 3 : 1}
                    sx={{
                      backgroundColor: darkMode ? "#1e1e1e" : "#ffffff", // Required for sticky to work well
                      borderBottom: "1px solid #ddd", // Optional: better separation
                    }}
                  >
                    <strong>{label}</strong>
                  </TableCell>
                ))}
              </TableRow>

              {/* My Data Row */}
              {my_data && (
                <TableRow
                  sx={{
                    backgroundColor: darkMode ? "#2d2d2d" : "#f0f9ff", // Soft background color
                    borderLeft: "4px solid", // Subtle accent bar
                    borderLeftColor: darkMode ? "#29b6f6" : "#0288d1", // Info-themed accent
                    boxShadow: darkMode
                      ? "inset 0 0 0 9999px rgba(255, 255, 255, 0.02)"
                      : "0px 2px 6px rgba(0, 0, 0, 0.05)",
                    fontWeight: "500",
                  }}
                >
                  <TableCell align="center">
                    <EmployeeAvatar photo={my_data.photo} />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={my_data.my_name}>
                      <Typography fontWeight="bold">
                        {my_data.my_name.length <= 12
                          ? my_data.my_name
                          : my_data.my_name.substr(0, 12) + "..."}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={my_data.job}>
                      <Typography>
                        {my_data.job.length <= 15 ? my_data.job : my_data.job.substr(0, 15) + "..."}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">{my_data.gauge}</TableCell>
                  <TableCell align="center" sx={{ fontSize: "2rem" }}>
                    {getTrendIcon(my_data.trend)}
                  </TableCell>
                  <TableCell colSpan={3}></TableCell> {/* No actions for self */}
                </TableRow>
              )}

              {/* Team Rows */}
              {employees.map((employee) => (
                <TableRow key={employee.index}>
                  <TableCell align="center">
                    <EmployeeAvatar photo={employee.photo} />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={employee.e_name}>
                      <Typography fontWeight="bold">
                        {employee.e_name.length <= 12
                          ? employee.e_name
                          : employee.e_name.substr(0, 12) + "..."}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={employee.job}>
                      <Typography>
                        {employee.job.length <= 15
                          ? employee.job
                          : employee.job.substr(0, 15) + "..."}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">{employee.gauge}</TableCell>
                  <TableCell align="center" sx={{ fontSize: "2rem" }}>
                    {getTrendIcon(employee.trend)}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      px: 0, // horizontal padding
                    }}
                  ></TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color="info"
                      onClick={() => setSelectedEmployee(employee.e_id)}
                      size="small"
                      startIcon={<Icon fontSize="small">groups</Icon>}
                    >
                      {t("user.myTeam.viewTeam")}
                    </Button>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color="info"
                      onClick={() => {
                        navigate(`/team/scorecard`, {
                          state: { employeeId: employee.e_id, month, year },
                        });
                      }}
                      size="small"
                      startIcon={<Icon fontSize="small">assessment</Icon>}
                    >
                      {t("user.myTeam.viewScorecard")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </TableContainer>
    </Box>
  );
};
ListView.propTypes = {
  month: PropTypes.number.isRequired,
  year: PropTypes.number.isRequired,
  setSelectedEmployee: PropTypes.object.isRequired,
  myTeam: PropTypes.array.isRequired,
  myIndividualData: PropTypes.array.isRequired,
};
export default ListView;
